import {
  Badge,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Tabs
} from '@mantine/core'
import { FormattedMessage } from 'react-intl'
import { DeploymentGroup } from '@/types/deploymentGroup'
import { Device } from '@/types/device'
import { getDeviceGpus } from '@/utils/device/getDeviceAttr'
import { DeviceMetricChart } from './DeviceMetricChart'
import { DeviceNetworkStats } from './DeviceNetworkStats'
import { GpuGroupsList, GpuLoadCharts } from './GpuLoadCharts'
import {
  TIME_RANGE_OPTIONS,
  formatCelsius,
  formatPercent,
  useMetricTimeRange
} from './metricTimeRanges'

type DeploymentObservabilityTabProps = {
  device: Device
  activeGpuIndexes: number[]
  appName: string
  groups?: DeploymentGroup[]
}

export const DeploymentObservabilityTab = ({
  device,
  activeGpuIndexes,
  appName,
  groups
}: DeploymentObservabilityTabProps) => {
  const { range, startTs, endTs, selectRange } = useMetricTimeRange()

  const gpus = getDeviceGpus(device)
  const gpuList = gpus.length > 0 ? gpus : [{ index: 0, name: 'GPU 0' }]

  const badgeFor = (gpuIndex: number) =>
    activeGpuIndexes.includes(gpuIndex) ? (
      <Badge size="sm" color="brand-primary" variant="light">
        {appName}
      </Badge>
    ) : undefined

  return (
    <Stack gap="md">
      <Tabs defaultValue="cpu-memory" variant="pills" keepMounted={false}>
        <Paper p="xs" radius="md" withBorder>
          <Group justify="space-between" align="center">
            <Tabs.List>
              <Tabs.Tab value="cpu-memory">
                <FormattedMessage id="deployments.details.observability.tab.cpuMemory" />
              </Tabs.Tab>
              <Tabs.Tab value="gpu">
                <FormattedMessage id="deployments.details.observability.tab.gpu" />
              </Tabs.Tab>
              <Tabs.Tab value="disk-network">
                <FormattedMessage id="deployments.details.observability.tab.diskNetwork" />
              </Tabs.Tab>
              <Tabs.Tab value="temperature">
                <FormattedMessage id="deployments.details.observability.tab.temperature" />
              </Tabs.Tab>
            </Tabs.List>

            <SegmentedControl
              data={TIME_RANGE_OPTIONS}
              value={range.label}
              w={200}
              size="xs"
              color="brand-primary"
              onChange={selectRange}
            />
          </Group>
        </Paper>

        <Tabs.Panel value="cpu-memory" pt="md">
          <Stack gap="md">
            <DeviceMetricChart
              deviceId={device.id}
              metricKey="cpu_usage_percent"
              title={
                <FormattedMessage
                  id="deployments.details.observability.cpu.title"
                  values={{ deviceName: device.label }}
                />
              }
              valueFormatter={formatPercent}
              tickFormatter={range.tickFormatter}
              startTs={startTs}
              endTs={endTs}
              agg={range.agg}
              interval={range.interval}
            />
            <DeviceMetricChart
              deviceId={device.id}
              metricKey="ram_usage_percent"
              title={
                <FormattedMessage
                  id="deployments.details.observability.ram.title"
                  values={{ deviceName: device.label }}
                />
              }
              valueFormatter={formatPercent}
              tickFormatter={range.tickFormatter}
              startTs={startTs}
              endTs={endTs}
              agg={range.agg}
              interval={range.interval}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="gpu" pt="md">
          <Stack gap="md">
            {gpuList.map((gpu) => (
              <Stack key={gpu.index} gap="md">
                <DeviceMetricChart
                  deviceId={device.id}
                  metricKey={`gpu_${gpu.index}_usage_percent`}
                  title={
                    <FormattedMessage
                      id="deployments.details.observability.gpu.title"
                      values={{ gpuIndex: gpu.index, gpuName: gpu.name }}
                    />
                  }
                  badge={badgeFor(gpu.index)}
                  footer={
                    <GpuGroupsList gpuIndex={gpu.index} groups={groups} />
                  }
                  valueFormatter={formatPercent}
                  tickFormatter={range.tickFormatter}
                  startTs={startTs}
                  endTs={endTs}
                  agg={range.agg}
                  interval={range.interval}
                />
                <GpuLoadCharts
                  deviceId={device.id}
                  gpu={gpu}
                  groups={groups}
                  range={range}
                  startTs={startTs}
                  endTs={endTs}
                />
              </Stack>
            ))}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="disk-network" pt="md">
          <Stack gap="md">
            <DeviceMetricChart
              deviceId={device.id}
              metricKey="disk_root_usage_percent"
              title={
                <FormattedMessage
                  id="deployments.details.observability.disk.title"
                  values={{ deviceName: device.label }}
                />
              }
              valueFormatter={formatPercent}
              tickFormatter={range.tickFormatter}
              startTs={startTs}
              endTs={endTs}
              agg={range.agg}
              interval={range.interval}
            />
            <DeviceNetworkStats
              deviceId={device.id}
              title={
                <FormattedMessage
                  id="deployments.details.observability.network.title"
                  values={{ deviceName: device.label }}
                />
              }
              rangeLabel={
                <FormattedMessage
                  id="deployments.details.observability.network.rangeLabel"
                  values={{ range: range.label }}
                />
              }
              startTs={startTs}
              endTs={endTs}
              interval={range.interval}
            />
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="temperature" pt="md">
          <Stack gap="md">
            <DeviceMetricChart
              deviceId={device.id}
              metricKey="cpu_temp_c"
              title={
                <FormattedMessage
                  id="deployments.details.observability.cpuTemp.title"
                  values={{ deviceName: device.label }}
                />
              }
              valueFormatter={formatCelsius}
              tickFormatter={range.tickFormatter}
              startTs={startTs}
              endTs={endTs}
              agg={range.agg}
              interval={range.interval}
            />
            {gpuList.map((gpu) => (
              <DeviceMetricChart
                key={gpu.index}
                deviceId={device.id}
                metricKey={`gpu_${gpu.index}_temp_c`}
                title={
                  <FormattedMessage
                    id="deployments.details.observability.gpuTemp.title"
                    values={{ gpuIndex: gpu.index, gpuName: gpu.name }}
                  />
                }
                badge={badgeFor(gpu.index)}
                valueFormatter={formatCelsius}
                tickFormatter={range.tickFormatter}
                startTs={startTs}
                endTs={endTs}
                agg={range.agg}
                interval={range.interval}
              />
            ))}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
}
